import pick from 'lodash/pick';
import config from '../../config';
import { confirmWhopPayment, initiatePrivileged, transitionPrivileged } from '../../util/api';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  TRANSITION_REQUEST_PAYMENT,
  TRANSITION_REQUEST_PAYMENT_AFTER_ENQUIRY,
  TRANSITION_CONFIRM_PAYMENT,
  isPrivileged,
  TRANSITION_REQUEST_PAYMENT_WHOP,
} from '../../util/transaction';
import * as log from '../../util/log';
import {
  fetchCurrentUserHasOrdersSuccess,
  fetchCurrentUser,
  currentUserShowSuccess,
} from '../../ducks/user.duck';

const { UUID } = sdkTypes;

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/CheckoutPage/SET_INITIAL_VALUES';

export const INITIATE_ORDER_REQUEST = 'app/CheckoutPage/INITIATE_ORDER_REQUEST';
export const INITIATE_ORDER_SUCCESS = 'app/CheckoutPage/INITIATE_ORDER_SUCCESS';
export const INITIATE_ORDER_ERROR = 'app/CheckoutPage/INITIATE_ORDER_ERROR';

export const CONFIRM_PAYMENT_REQUEST =
  'app/CheckoutPage/CONFIRM_PAYMENT_REQUEST';
export const CONFIRM_PAYMENT_SUCCESS =
  'app/CheckoutPage/CONFIRM_PAYMENT_SUCCESS';
export const CONFIRM_PAYMENT_ERROR = 'app/CheckoutPage/CONFIRM_PAYMENT_ERROR';

export const SPECULATE_TRANSACTION_REQUEST =
  'app/ListingPage/SPECULATE_TRANSACTION_REQUEST';
export const SPECULATE_TRANSACTION_WITH_EXTRAS_REQUEST =
  'app/ListingPage/SPECULATE_TRANSACTION_WITH_EXTRAS_REQUEST';
export const SPECULATE_TRANSACTION_SUCCESS =
  'app/ListingPage/SPECULATE_TRANSACTION_SUCCESS';
export const SPECULATE_TRANSACTION_ERROR =
  'app/ListingPage/SPECULATE_TRANSACTION_ERROR';

export const STRIPE_CUSTOMER_REQUEST =
  'app/CheckoutPage/STRIPE_CUSTOMER_REQUEST';
export const STRIPE_CUSTOMER_SUCCESS =
  'app/CheckoutPage/STRIPE_CUSTOMER_SUCCESS';
export const STRIPE_CUSTOMER_ERROR = 'app/CheckoutPage/STRIPE_CUSTOMER_ERROR';

export const SEND_MESSAGE_REQUEST = 'app/CheckoutPage/SEND_MESSAGE_REQUEST';
export const SEND_MESSAGE_SUCCESS = 'app/CheckoutPage/SEND_MESSAGE_SUCCESS';
export const SEND_MESSAGE_ERROR = 'app/CheckoutPage/SEND_MESSAGE_ERROR';

// ================ Reducer ================ //

const initialState = {
  listing: null,
  bookingData: null,
  bookingDates: null,
  speculateTransactionInProgress: false,
  speculateTransactionError: null,
  speculatedTransaction: null,
  transaction: null,
  initiateOrderError: null,
  confirmPaymentError: null,
  stripeCustomerFetched: false,
  speculateTransactionWithExtrasInProgress: false,
  sendMessageInProgress: false,
};

const normalizeCustomerTermsValue = value => {
  if (!value) {
    return null;
  }
  if (Array.isArray(value)) {
    return value.includes('accepted') ? 'accepted' : null;
  }
  return value === true || value === 'accepted' ? 'accepted' : null;
};

export default function checkoutPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SEND_MESSAGE_REQUEST:
      return { ...state, sendMessageInProgress: true };
    case SEND_MESSAGE_SUCCESS:
      return { ...state, sendMessageInProgress: false };
    case SEND_MESSAGE_ERROR:
      return { ...state, sendMessageInProgress: false };

    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case SPECULATE_TRANSACTION_REQUEST:
      return {
        ...state,
        speculateTransactionInProgress: true,
        speculateTransactionError: null,
        speculatedTransaction: null,
        speculateTransactionWithExtrasInProgress: true,
      };
    case SPECULATE_TRANSACTION_WITH_EXTRAS_REQUEST:
      return {
        ...state,
        speculateTransactionWithExtrasInProgress: true,
        speculateTransactionError: null,
      };
    case SPECULATE_TRANSACTION_SUCCESS:
      return {
        ...state,
        speculateTransactionInProgress: false,
        speculatedTransaction: payload.transaction,
        speculateTransactionWithExtrasInProgress: false,
      };
    case SPECULATE_TRANSACTION_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return {
        ...state,
        speculateTransactionInProgress: false,
        speculateTransactionError: payload,
        speculateTransactionWithExtrasInProgress: false,
      };

    case INITIATE_ORDER_REQUEST:
      return { ...state, initiateOrderError: null };
    case INITIATE_ORDER_SUCCESS:
      return { ...state, transaction: payload };
    case INITIATE_ORDER_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, initiateOrderError: payload };

    case CONFIRM_PAYMENT_REQUEST:
      return { ...state, confirmPaymentError: null };
    case CONFIRM_PAYMENT_SUCCESS:
      return state;
    case CONFIRM_PAYMENT_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, confirmPaymentError: payload };

    case STRIPE_CUSTOMER_REQUEST:
      return { ...state, stripeCustomerFetched: false };
    case STRIPE_CUSTOMER_SUCCESS:
      return { ...state, stripeCustomerFetched: true };
    case STRIPE_CUSTOMER_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, stripeCustomerFetchError: payload };

    default:
      return state;
  }
}

// ================ Selectors ================ //

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

const initiateOrderRequest = () => ({ type: INITIATE_ORDER_REQUEST });

const initiateOrderSuccess = order => ({
  type: INITIATE_ORDER_SUCCESS,
  payload: order,
});

const initiateOrderError = e => ({
  type: INITIATE_ORDER_ERROR,
  error: true,
  payload: e,
});

const confirmPaymentRequest = () => ({ type: CONFIRM_PAYMENT_REQUEST });

const confirmPaymentSuccess = orderId => ({
  type: CONFIRM_PAYMENT_SUCCESS,
  payload: orderId,
});

const confirmPaymentError = e => ({
  type: CONFIRM_PAYMENT_ERROR,
  error: true,
  payload: e,
});

const sendMessageRequest = () => ({ type: SEND_MESSAGE_REQUEST });

const sendMessageSuccess = () => ({
  type: SEND_MESSAGE_SUCCESS,
});

const sendMessageError = e => ({
  type: SEND_MESSAGE_ERROR,
  error: true,
  payload: e,
});

export const speculateTransactionRequest = () => ({
  type: SPECULATE_TRANSACTION_REQUEST,
});
export const speculateTransactionWithExtrasRequest = () => ({
  type: SPECULATE_TRANSACTION_WITH_EXTRAS_REQUEST,
});

export const speculateTransactionSuccess = transaction => ({
  type: SPECULATE_TRANSACTION_SUCCESS,
  payload: { transaction },
});

export const speculateTransactionError = e => ({
  type: SPECULATE_TRANSACTION_ERROR,
  error: true,
  payload: e,
});

export const stripeCustomerRequest = () => ({ type: STRIPE_CUSTOMER_REQUEST });
export const stripeCustomerSuccess = () => ({ type: STRIPE_CUSTOMER_SUCCESS });
export const stripeCustomerError = e => ({
  type: STRIPE_CUSTOMER_ERROR,
  error: true,
  payload: e,
});

/* ================ Thunks ================ */

export const initiateOrder = (orderParams, transactionId) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(initiateOrderRequest());
  const method = orderParams?.method;
  // If we already have a transaction ID, we should transition, not
  // initiateonst

  const isTransition = !!transactionId;
  const transition = isTransition
    ? TRANSITION_REQUEST_PAYMENT_AFTER_ENQUIRY
    : orderParams?.isWhop
    ? TRANSITION_REQUEST_PAYMENT_WHOP
    : TRANSITION_REQUEST_PAYMENT;
  const isPrivilegedTransition = isPrivileged(transition);

  const bookingData = {
    extras: orderParams?.extras ?? [],
    selectedPlan: orderParams?.selectedPlan ?? null,
  };

  const bodyParams = isTransition
    ? {
        id: transactionId,
        transition,
        params: orderParams,
      }
    : {
        processAlias:
          method == 'online'
            ? config.bookingProcessAliasOnline
            : config.bookingProcessAlias,
        transition,
        params: orderParams,
      };
  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };

  const handleSucces = response => {
    const entities = denormalisedResponseEntities(response);
    const order = entities[0];
    dispatch(initiateOrderSuccess(order));
    dispatch(fetchCurrentUserHasOrdersSuccess(true));
    return order;
  };

  const handleError = e => {
    dispatch(initiateOrderError(storableError(e)));
    const transactionIdMaybe = transactionId
      ? { transactionId: transactionId.uuid }
      : {};
    log.error(e, 'initiate-order-failed', {
      ...transactionIdMaybe,
      listingId: orderParams.listingId.uuid,
      bookingStart: orderParams.bookingStart,
      bookingEnd: orderParams.bookingEnd,
    });
    throw e;
  };

  if (isTransition && isPrivilegedTransition) {
    // transition privileged
    return transitionPrivileged({
      isSpeculative: false,
      bookingData,
      bodyParams,
      queryParams,
    })
      .then(handleSucces)
      .catch(handleError);
  } else if (isTransition) {
    // transition non-privileged
    return sdk.transactions
      .transition(bodyParams, queryParams)
      .then(handleSucces)
      .catch(handleError);
  } else if (isPrivilegedTransition) {
    // initiate privileged
    return initiatePrivileged({
      isSpeculative: false,
      bookingData,
      bodyParams,
      queryParams,
    })
      .then(handleSucces)
      .catch(handleError);
  } else {
    // initiate non-privileged
    return sdk.transactions
      .initiate(bodyParams, queryParams)
      .then(handleSucces)
      .catch(handleError);
  }
};

export const confirmPayment = orderParams => (dispatch, getState, sdk) => {
  dispatch(confirmPaymentRequest());
  const { isWhop, params = {}, transactionId } = orderParams;

  if (isWhop) {
    const whopDetails = params?.protectedData?.whopDetails || {};
    return confirmWhopPayment({ transactionId, whopDetails })
      .then(apiResponse => {
        const order = apiResponse.data;
        dispatch(confirmPaymentSuccess(order));
        return order;
      })
      .catch(e => {
        // Pending is the normal in-flight state for async methods (Cash App Pay)
        // while the checkout polls — not a failure worth reporting.
        if (e?.code !== 'WHOP_PAYMENT_PENDING') {
          dispatch(confirmPaymentError(storableError(e)));
          const transactionIdMaybe = transactionId ? { transactionId: transactionId.uuid || transactionId } : {};
          log.error(e, 'confirm-whop-payment-failed', {
            ...transactionIdMaybe,
          });
        }
        throw e;
      });
  }

  const bodyParams = {
    id: transactionId,
    transition: TRANSITION_CONFIRM_PAYMENT,
    params,
  };
  const queryParams = {
    expand: true,
  };

  return sdk.transactions
    .transition(bodyParams, queryParams)
    .then(response => {
      const order = response.data.data;
      dispatch(confirmPaymentSuccess(order));
      return order;
    })
    .catch(e => {
      dispatch(confirmPaymentError(storableError(e)));
      const transactionIdMaybe = orderParams.transactionId
        ? { transactionId: orderParams.transactionId.uuid }
        : {};
      log.error(e, 'initiate-order-failed', {
        ...transactionIdMaybe,
      });
      throw e;
    });
};

// Re-read an order the buyer already placed.
//
// Whop Elements finishes a checkout by redirecting the buyer's tab, so the page
// that comes back has lost the in-memory order from before the payment. Fetch it
// through the SDK rather than reusing the confirm-payment response: that one is
// plain JSON, and BookingBreakdown needs the SDK's Money/UUID types.
export const fetchWhopOrder = transactionId => (dispatch, getState, sdk) =>
  sdk.transactions
    .show(
      {
        // The id arrives as a bare string when it comes off the return URL.
        id: typeof transactionId === 'string' ? new UUID(transactionId) : transactionId,
        include: ['booking', 'provider', 'customer', 'listing'],
      },
      { expand: true }
    )
    .then(response => {
      const [order] = denormalisedResponseEntities(response);
      if (!order) {
        throw new Error(`Order ${transactionId} not found`);
      }
      return order;
    });

export const sendMessage = params => (dispatch, getState, sdk) => {
  let message = params.message;
  const orderId = params.id;
  const type = params.type;
  const order = params.order;

  if (type == 'checkout') {
    return {
      orderId,
      messageSuccess: true,
      order,
    };
  }

  dispatch(sendMessageRequest());

  if (message) {
    message = message.trim();
    return sdk.messages
      .send({ transactionId: orderId, content: message })
      .then(() => {
        dispatch(sendMessageSuccess());
        return { orderId, messageSuccess: true, order };
      })
      .catch(e => {
        dispatch(sendMessageError(storableError(e)));
        log.error(e, 'initial-message-send-failed', { txId: orderId });
        return { orderId, messageSuccess: false, order };
      });
  } else {
    return Promise.resolve({ orderId, messageSuccess: true, order });
  }
};

export const saveCustomerTerms = acceptTermsValue => (dispatch, getState, sdk) => {
  const normalizedValue = normalizeCustomerTermsValue(acceptTermsValue);
  if (!normalizedValue) {
    return Promise.resolve(null);
  }

  const currentUser = getState()?.user?.currentUser;
  const existingValue =
    currentUser?.attributes?.profile?.publicData?.acceptCustomerTerms;

  if (!currentUser?.id || existingValue === normalizedValue) {
    return Promise.resolve(currentUser || null);
  }

  const currentPublicData = currentUser.attributes?.profile?.publicData || {};
  const publicData = {
    ...currentPublicData,
    acceptCustomerTerms: normalizedValue,
  };

  return sdk.currentUser
    .updateProfile({ publicData }, { expand: true })
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
      }
      const updatedUser = entities[0];
      dispatch(currentUserShowSuccess(updatedUser));
      return updatedUser;
    })
    .catch(e => {
      log.error(e, 'save-customer-terms-failed');
      throw e;
    });
};

/**
 * Initiate or transition the speculative transaction with the given
 * booking details
 *
 * The API allows us to do speculative transaction initiation and
 * transitions. This way we can create a test transaction and get the
 * actual pricing information as if the transaction had been started,
 * without affecting the actual data.
 *
 * We store this speculative transaction in the page store and use the
 * pricing info for the booking breakdown to get a proper estimate for
 * the price with the chosen information.
 */
export const speculateTransaction = (orderParams, transactionId) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(speculateTransactionRequest());

  // If we already have a transaction ID, we should transition, not
  // initiate.
  const method = orderParams?.method;
  const isTransition = !!transactionId;
  // Whop checkout books via request-payment-whop; speculating with the stripe
  // transition would require the provider to have a Stripe account.
  const transition = isTransition
    ? TRANSITION_REQUEST_PAYMENT_AFTER_ENQUIRY
    : config.isWhopCheckout
    ? TRANSITION_REQUEST_PAYMENT_WHOP
    : TRANSITION_REQUEST_PAYMENT;
  const isPrivilegedTransition = isPrivileged(transition);

  const bookingData = {
    startDate: orderParams.bookingStart,
    endDate: orderParams.bookingEnd,
    extras: orderParams?.extras ?? [],
    selectedPlan: orderParams?.selectedPlan ?? null,
  };

  const params = {
    ...orderParams,
    cardToken: 'CheckoutPage_speculative_card_token',
  };

  const bodyParams = isTransition
    ? {
        id: transactionId,
        transition,
        params,
      }
    : {
        processAlias:
          method == 'online'
            ? config.bookingProcessAliasOnline
            : config.bookingProcessAlias,
        transition,
        params,
      };

  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };

  const handleSuccess = response => {
    const entities = denormalisedResponseEntities(response);
    if (entities.length !== 1) {
      throw new Error('Expected a resource in the speculate response');
    }
    const tx = entities[0];
    dispatch(speculateTransactionSuccess(tx));
  };

  const handleError = e => {
    const { listingId, bookingStart, bookingEnd } = params;
    log.error(e, 'speculate-transaction-failed', {
      listingId: listingId.uuid,
      bookingStart,
      bookingEnd,
    });
    return dispatch(speculateTransactionError(storableError(e)));
  };

  if (isTransition && isPrivilegedTransition) {
    // transition privileged
    return transitionPrivileged({
      isSpeculative: true,
      bookingData,
      bodyParams,
      queryParams,
    })
      .then(handleSuccess)
      .catch(handleError);
  } else if (isTransition) {
    // transition non-privileged
    return sdk.transactions
      .transitionSpeculative(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  } else if (isPrivilegedTransition) {
    // initiate privileged
    return initiatePrivileged({
      isSpeculative: true,
      bookingData,
      bodyParams,
      queryParams,
    })
      .then(handleSuccess)
      .catch(handleError);
  } else {
    // initiate non-privileged
    return sdk.transactions
      .initiateSpeculative(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  }
};
export const speculateTransactionWithExtras = (orderParams, transactionId) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(speculateTransactionWithExtrasRequest());

  // If we already have a transaction ID, we should transition, not
  // initiate.
  const isTransition = !!transactionId;
  // Whop checkout books via request-payment-whop; speculating with the stripe
  // transition would require the provider to have a Stripe account.
  const transition = isTransition
    ? TRANSITION_REQUEST_PAYMENT_AFTER_ENQUIRY
    : config.isWhopCheckout
    ? TRANSITION_REQUEST_PAYMENT_WHOP
    : TRANSITION_REQUEST_PAYMENT;
  const isPrivilegedTransition = isPrivileged(transition);

  const bookingData = {
    startDate: orderParams.bookingStart,
    endDate: orderParams.bookingEnd,
    extras: orderParams?.extras ?? [],
    selectedPlan: orderParams?.selectedPlan ?? null,
  };

  const params = {
    ...orderParams,
    cardToken: 'CheckoutPage_speculative_card_token',
  };

  const bodyParams = isTransition
    ? {
        id: transactionId,
        transition,
        params,
      }
    : {
        processAlias: config.bookingProcessAlias,
        transition,
        params,
      };

  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };

  const handleSuccess = response => {
    const entities = denormalisedResponseEntities(response);
    if (entities.length !== 1) {
      throw new Error('Expected a resource in the speculate response');
    }
    const tx = entities[0];
    dispatch(speculateTransactionSuccess(tx));
  };

  const handleError = e => {
    const { listingId, bookingStart, bookingEnd } = params;
    log.error(e, 'speculate-transaction-failed', {
      listingId: listingId.uuid,
      bookingStart,
      bookingEnd,
    });
    return dispatch(speculateTransactionError(storableError(e)));
  };

  if (isTransition && isPrivilegedTransition) {
    // transition privileged
    return transitionPrivileged({
      isSpeculative: true,
      bookingData,
      bodyParams,
      queryParams,
    })
      .then(handleSuccess)
      .catch(handleError);
  } else if (isTransition) {
    // transition non-privileged
    return sdk.transactions
      .transitionSpeculative(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  } else if (isPrivilegedTransition) {
    // initiate privileged
    return initiatePrivileged({
      isSpeculative: true,
      bookingData,
      bodyParams,
      queryParams,
    })
      .then(handleSuccess)
      .catch(handleError);
  } else {
    // initiate non-privileged
    return sdk.transactions
      .initiateSpeculative(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  }
};

// StripeCustomer is a relantionship to currentUser
// We need to fetch currentUser with correct params to include relationship
export const stripeCustomer = () => (dispatch, getState, sdk) => {
  dispatch(stripeCustomerRequest());

  return dispatch(
    fetchCurrentUser({ include: ['stripeCustomer.defaultPaymentMethod'] })
  )
    .then(response => {
      dispatch(stripeCustomerSuccess());
    })
    .catch(e => {
      dispatch(stripeCustomerError(storableError(e)));
    });
};
